/**
 * Dashboard widget components for chapter home pages.
 * PrimaryWidget, ProgressWidget, WomenLikeYou, LiveRooms, CommunityPreview
 */
import SectionHeader from '../SectionHeader';

const FONT = '"Plus Jakarta Sans", -apple-system, sans-serif';

const cardBase = {
  background: 'var(--card)',
  border: '1px solid var(--border)',
  borderRadius: 16,
  padding: 20,
};

function WidgetHeaderRow({ title, descriptor, trailing }) {
  return (
    <div
      style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        gap: 10,
        marginBottom: 14,
        flexWrap: 'wrap',
      }}
    >
      <div style={{ flex: '1 1 200px', minWidth: 0 }}>
        <SectionHeader title={title} descriptor={descriptor} />
      </div>
      {trailing ? <div style={{ display: 'flex', alignItems: 'center', flexShrink: 0 }}>{trailing}</div> : null}
    </div>
  );
}

/** Left widget: primary data (meds, cycle info, symptoms, baby, tracker) */
export function PrimaryWidget({ theme, title, descriptor, linkLabel, onLink, rows }) {
  const trailing =
    linkLabel && onLink ? (
      <button
        type="button"
        onClick={onLink}
        style={{ fontSize: 12, fontWeight: 500, color: theme.accentText, background: 'none', border: 'none', cursor: 'pointer', fontFamily: FONT }}
      >
        {linkLabel}
      </button>
    ) : null;
  return (
    <div style={cardBase}>
      <WidgetHeaderRow title={title} descriptor={descriptor} trailing={trailing} />
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {rows.map((r, i) => (
          <div key={i} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <span style={{ fontSize: 13, color: 'var(--text-2)' }}>{r.label}</span>
            <span style={{ fontSize: 13, color: 'var(--muted)' }}>{r.value}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

/** Right widget: journey progress / step tracker */
export function ProgressWidget({ theme, title, descriptor, steps, linkLabel, onLink }) {
  const trailing =
    linkLabel && onLink ? (
      <button
        type="button"
        onClick={onLink}
        style={{ fontSize: 12, fontWeight: 500, color: theme.accentText, background: 'none', border: 'none', cursor: 'pointer', fontFamily: FONT }}
      >
        {linkLabel}
      </button>
    ) : null;
  return (
    <div style={cardBase}>
      <WidgetHeaderRow title={title} descriptor={descriptor} trailing={trailing} />
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
        {steps.map((s) => (
          <div key={s.number} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
            <div
              style={{
                width: 36,
                height: 36,
                borderRadius: 12,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: 12,
                fontWeight: 600,
                fontFamily: FONT,
                background: s.done ? theme.accentBg : s.active ? theme.postButtonBg : 'var(--sand)',
                color: s.done ? theme.accentPillText : s.active ? 'white' : 'var(--muted)',
              }}
            >
              {s.done ? '✓' : s.number}
            </div>
            <span style={{ fontSize: 10, color: 'var(--muted)', textAlign: 'center', lineHeight: 1.2, maxWidth: 36 }}>{s.label}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

/** Women Like You widget */
export function WomenLikeYou({ theme, descriptor, users, onSeeAll }) {
  return (
    <div style={cardBase}>
      <WidgetHeaderRow
        title="Women like you"
        descriptor={descriptor}
        trailing={
          <button
            type="button"
            onClick={onSeeAll}
            style={{ fontSize: 12, fontWeight: 500, color: theme.accentText, background: 'none', border: 'none', cursor: 'pointer', fontFamily: FONT }}
          >
            See all →
          </button>
        }
      />
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        {users.map((u, i) => (
          <div key={i} style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
            <div
              style={{
                width: 36,
                height: 36,
                borderRadius: '50%',
                background: 'var(--sand)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: 13,
                fontWeight: 600,
                color: 'var(--muted)',
                flexShrink: 0,
                overflow: 'hidden',
              }}
            >
              {u.avatarUrl ? <img src={u.avatarUrl} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : u.initial}
            </div>
            <div style={{ minWidth: 0 }}>
              <p style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)', margin: 0 }}>{u.name}</p>
              <p style={{ fontSize: 12, color: 'var(--muted)', margin: 0 }}>{u.description}</p>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

/** Live Rooms widget */
export function LiveRooms({ theme, descriptor, rooms, onSeeAll }) {
  return (
    <div style={cardBase}>
      <WidgetHeaderRow
        title="Live rooms"
        descriptor={descriptor}
        trailing={
          <button
            type="button"
            onClick={onSeeAll}
            style={{ fontSize: 12, fontWeight: 500, color: theme.accentText, background: 'none', border: 'none', cursor: 'pointer', fontFamily: FONT }}
          >
            See all →
          </button>
        }
      />
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {rooms.map((r, i) => (
          <div key={i} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <span style={{ fontSize: 13, color: 'var(--text-2)' }}>{r.name}</span>
            <span style={{ fontSize: 12, color: 'var(--sage)', fontWeight: 500 }}>{r.count} online</span>
          </div>
        ))}
      </div>
    </div>
  );
}

/** Community preview widget */
export function CommunityPreview({ theme, descriptor, posts, onSeeAll }) {
  return (
    <div style={cardBase}>
      <WidgetHeaderRow
        title="Community"
        descriptor={descriptor}
        trailing={
          <button
            type="button"
            onClick={onSeeAll}
            style={{ fontSize: 12, fontWeight: 500, color: theme.accentText, background: 'none', border: 'none', cursor: 'pointer', fontFamily: FONT }}
          >
            See all →
          </button>
        }
      />
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        {posts.map((p, i) => (
          <div key={i} style={{ background: 'var(--sand)', borderRadius: 12, padding: 16 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
              <div
                style={{
                  width: 28,
                  height: 28,
                  borderRadius: '50%',
                  background: 'var(--border)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: 12,
                  fontWeight: 600,
                  color: 'var(--muted)',
                }}
              >
                {p.authorInitial}
              </div>
              <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)' }}>{p.authorName}</span>
              <span style={{ fontSize: 12, color: 'var(--muted)' }}>{p.timeAgo}</span>
            </div>
            <p style={{ fontSize: 13, color: 'var(--text-2)', lineHeight: 1.5, margin: 0 }}>{p.text}</p>
            <div style={{ display: 'flex', gap: 12, marginTop: 8, fontSize: 12, color: 'var(--muted)' }}>
              <span>❤️ {p.likes}</span>
              <span>🤝 same boat {p.sameBoat}</span>
              <span>💬 {p.replies}</span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
